import {getAPIGatewayDataFromParent} from "../../handler/ParentDataHandler";
import {logOperationalMetric} from "../../util/CommonUtils";
import {EWASTE_API_PATHS, HTTP_METHOD, PROCESS_NAME} from "../constants/EWasteApiPaths";
import {toCanonicalWasteCode, toWasteProfileId} from "../constants/EWasteConfig";

const metricProcessName = "SCC_EWASTE_ITEM_CREATE";
const SUB_PROCESS = "createEWasteItem";

/**
 * Calls OculusService.CreateEWasteItem via Boson FlowApiGateway.
 *
 * @param {string} containerId       Parent container UUID.
 * @param {string} itemName          Display name (e.g. "MacBook Pro 13").
 * @param {string} wasteProfileId    Waste code, canonical ("20 01 36") or compact ("200136").
 * @param {number} quantity          Item count (>= 1).
 * @param {{weight: number, unit: string}} totalWeight  EWasteWeight struct.
 * @param {string} locale            WADM locale (e.g. "en_GB").
 *
 * Note: 3P has no Hummingbird interaction (Hummingbird maps wasteProfileId →
 * ewcCode for 1P), so both fields are derived from the same code here:
 *   - `wasteProfileId` is sent compact ("200136") to match the container's stored value
 *     (see {@link toWasteProfileId}) so OculusService's one-container-one-EWC guard holds;
 *   - `ewcCode` is sent canonical ("20 01 36") because it is what the WTN / compliance
 *     documents and the SCC item views display.
 * @returns {Promise<{success: boolean, itemId?: string, status?: string, reason?: string}>}
 *   On failure, {@code reason} is set to "EWC_MISMATCH" when OculusService rejected the
 *   request (HTTP 400) because the item's waste code does not match the container's; callers
 *   can map it to a specific message instead of a generic failure.
 */
export async function createEWasteItem(containerId, itemName, wasteProfileId, quantity, totalWeight, locale) {
    const startTime = Date.now();
    const requestBody = {
        containerId,
        itemName,
        wasteProfileId: toWasteProfileId(wasteProfileId),
        // 3P: no Hummingbird mapping; ewcCode is the canonical (spaced) EWC code.
        ewcCode: toCanonicalWasteCode(wasteProfileId),
        quantity,
        totalWeight,
        locale
    };

    const request = {
        resourcePath: EWASTE_API_PATHS.CREATE_ITEM,
        httpMethod: HTTP_METHOD.POST,
        processName: PROCESS_NAME.OCULUS,
        requestBody
    };

    try {
        const response = await getAPIGatewayDataFromParent(request);
        if (response.status === 200) {
            logOperationalMetric(startTime, metricProcessName, SUB_PROCESS, true);
            return {
                success: true,
                itemId: response.data && response.data.itemId,
                status: response.data && response.data.status
            };
        }
        logOperationalMetric(startTime, metricProcessName, SUB_PROCESS, false);
        return {success: false, reason: resolveFailureReason(response)};
    } catch (error) {
        logOperationalMetric(startTime, metricProcessName, SUB_PROCESS, false);
        return {success: false, reason: resolveFailureReason(error && error.response)};
    }
}

/** Reason token returned when the item's waste code does not match its container. */
export const EWC_MISMATCH_REASON = "EWC_MISMATCH";

/**
 * Extracts a machine-readable failure reason from a non-200 gateway response. Returns
 * "EWC_MISMATCH" when a 400 carries the EWC_MISMATCH marker (in message or reason), else
 * undefined so callers fall back to the generic failure message.
 */
function resolveFailureReason(response) {
    if (!response || response.status !== 400) {
        return undefined;
    }
    const data = response.data || {};
    const text = `${data.reason || ""} ${data.message || ""}`;
    return text.indexOf(EWC_MISMATCH_REASON) !== -1 ? EWC_MISMATCH_REASON : undefined;
}



// WEBPACK FOOTER //
// ./src/commons/api/CreateEWasteItem.js